import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import { getThemeTokens } from '../../../theme/tokens';
import { EntityPanelFrame } from './EntityPanelFrame';
import { Sparkline } from './Sparkline';
import {
  LINEARB_TEAM_ANNOTATION,
  useLinearBDora,
  type DoraBand,
  type DoraMetric,
} from './useLinearBDora';

const METRIC_TOOLTIPS: Record<string, string> = {
  deployFrequency: 'How often the team successfully deploys to production.',
  leadTime:
    'Time from a commit being merged until it is running in production.',
  changeFailureRate:
    'Percentage of deployments that require a hotfix, rollback, or patch.',
  mttr: 'How long it takes to restore service after a production incident.',
};

function InfoIcon({ title }: { title: string }) {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  return (
    <Tooltip title={title} arrow>
      <InfoOutlinedIcon
        sx={{
          color: tokens.textMuted,
          cursor: 'help',
          fontSize: 13,
          flexShrink: 0,
          opacity: 0.7,
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  );
}

// LinearB brand mark — rising step bars, brand purple #6C47FF
const LinearBIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ display: 'block', flexShrink: 0 }}
    aria-hidden
  >
    <rect x="2" y="15" width="5" height="7" rx="1" fill="#6C47FF" />
    <rect x="9.5" y="9" width="5" height="13" rx="1" fill="#6C47FF" />
    <rect x="17" y="2" width="5" height="20" rx="1" fill="#6C47FF" />
  </svg>
);

// Band labels and semantic palette colours (Elite=success, High=primary, Medium=warning, Low=error)
const BAND_CONFIG: Record<
  DoraBand,
  { label: string; paletteKey: 'success' | 'primary' | 'warning' | 'error' }
> = {
  elite: { label: 'Elite', paletteKey: 'success' },
  high: { label: 'High', paletteKey: 'primary' },
  medium: { label: 'Medium', paletteKey: 'warning' },
  low: { label: 'Low', paletteKey: 'error' },
};

function BandChip({ band }: { band: DoraBand }) {
  const theme = useTheme();
  const { label, paletteKey } = BAND_CONFIG[band];
  const color = theme.palette[paletteKey].main;

  return (
    <Box
      component="span"
      sx={{
        alignSelf: 'flex-start',
        backgroundColor: alpha(color, 0.12),
        borderRadius: '4px',
        color,
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        px: 0.75,
        py: 0.25,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Box>
  );
}

function MetricTile({ metric }: { metric: DoraMetric }) {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const accentColor = theme.palette.primary.main;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}
    >
      {/* Label + info icon */}
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {metric.label}
        </Typography>
        {METRIC_TOOLTIPS[metric.key] && (
          <InfoIcon title={METRIC_TOOLTIPS[metric.key]} />
        )}
      </Box>

      {/* Value + chip on left, sparkline on right */}
      <Box
        sx={{ alignItems: 'center', display: 'flex', gap: 1.5, minWidth: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {metric.displayValue}
          </Typography>
          {metric.band ? (
            <BandChip band={metric.band} />
          ) : (
            <Box sx={{ height: 20 }} />
          )}
        </Box>

        {metric.trend.length >= 2 && (
          <Box
            sx={{
              backgroundColor: alpha(accentColor, 0.06),
              borderRadius: 1,
              flex: '1 1 0',
              minWidth: 0,
              p: 0.75,
            }}
          >
            <Sparkline points={metric.trend} color={accentColor} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export const EntityDoraMetricsCard = () => {
  const { entity } = useEntity();
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  const teamId = entity.metadata.annotations?.[LINEARB_TEAM_ANNOTATION];
  const hasAnnotation = Boolean(teamId);
  const teamUrl = `https://app.linearb.io/dashboard?teams=${teamId}`;

  const { loading, error, data } = useLinearBDora(entity, 90);

  // No annotation → invisible (don't render at all, keeps the Grid clean)
  if (!hasAnnotation) return null;

  const accentColor = theme.palette.primary.main;

  const linkAction = (
    <Link to={teamUrl} target="_blank" rel="noopener noreferrer">
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
        <LinearBIcon size={14} />
        <Typography sx={{ color: accentColor, fontSize: 12, fontWeight: 700 }}>
          View in LinearB ↗
        </Typography>
      </Box>
    </Link>
  );

  if (loading) {
    return (
      <EntityPanelFrame title="DORA metrics" action={linkAction}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
          }}
        >
          {[0, 1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
            >
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" height={28} />
              <Skeleton
                variant="rectangular"
                height={24}
                width={60}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={36}
                sx={{ borderRadius: 1, mt: 0.5 }}
              />
            </Box>
          ))}
        </Box>
      </EntityPanelFrame>
    );
  }

  if (error || !data) {
    const placeholders = [
      { key: 'deployFrequency', label: 'Deploy Frequency' },
      { key: 'leadTime', label: 'Lead Time' },
      { key: 'changeFailureRate', label: 'Change Fail Rate' },
      { key: 'mttr', label: 'Mean Time to Restore' },
    ];
    return (
      <EntityPanelFrame title="DORA metrics" action={linkAction}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, mb: 2 }}>
          <Typography
            sx={{
              color: tokens.textMuted,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Last 90 days
          </Typography>
          <InfoIcon title="Covers the last 13 complete Mon–Sun weeks, aligned to LinearB's weekly reporting." />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
          {placeholders.map(({ key, label }) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                <Typography
                  sx={{
                    color: tokens.textMuted,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {label}
                </Typography>
                {METRIC_TOOLTIPS[key] && (
                  <InfoIcon title={METRIC_TOOLTIPS[key]} />
                )}
              </Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: tokens.textMuted,
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                    }}
                  >
                    —
                  </Typography>
                  <Box sx={{ height: 20 }} />
                </Box>
                <Box
                  sx={{
                    backgroundColor: alpha(accentColor, 0.04),
                    borderRadius: 1,
                    flex: '1 1 0',
                    minWidth: 0,
                    p: 0.75,
                    height: 36,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </EntityPanelFrame>
    );
  }

  return (
    <EntityPanelFrame title="DORA metrics" action={linkAction}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, mb: 2 }}>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Last 90 days
        </Typography>
        <InfoIcon title="Covers the last 13 complete Mon–Sun weeks, aligned to LinearB's weekly reporting." />
      </Box>

      {/* 2×2 metric grid — use CSS grid, not MUI Grid spacing (which won't show gaps in this theme) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2.5,
        }}
      >
        {data.metrics.map(metric => (
          <MetricTile key={metric.key} metric={metric} />
        ))}
      </Box>
    </EntityPanelFrame>
  );
};
